import { useEffect, useMemo } from 'react';
import { Box, MenuItem, Select, styled, useTheme } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import { NETWORK_SELECTION, SUPPORTED_NETWORKS, DEFAULT_NETWORK } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';

const NetworkIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'forceNativeColor',
})(({ color, forceNativeColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 37,
  minWidth: 40,
  borderRadius: 0,
  background: color,

  '& svg': {
    width: 28,
    height: 28,
    '& path': forceNativeColor ? undefined : { fill: 'white' },
  },
}));

const NetworkSelect = () => {
  const theme = useTheme();

  const location = useLocation();
  const {
    isConnected,
    globalChain,
    chainId,
    storedChain,
    updateStoredChain,
    switchToNetwork,
    setWrongNetwork,
  } = useWalletContext();
  const history = useHistory();

  const handleToggleNetwork = async (targetChain) => {
    try {
      if (globalChain !== targetChain) {
        let switchResult;
        if (isConnected) {
          switchResult = await switchToNetwork(targetChain);
        }
        if (switchResult?.id === targetChain) {
          updateStoredChain(targetChain);
          return;
        }

        updateStoredChain(targetChain);
        if (!location.pathname.includes('/launchpad')) {
          history.push('/collections?type=top');
        }
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const NETWORK_FILL = {
    [SUPPORTED_NETWORKS.Energi]: theme.palette.primary.main,
    [SUPPORTED_NETWORKS.Ethereum]: theme.palette.background.ethereum,
    [SUPPORTED_NETWORKS.Base]: theme.palette.background.base,
    [SUPPORTED_NETWORKS.ApeChain]: theme.palette.background.apechain,
    [SUPPORTED_NETWORKS.Berachain]: theme.palette.background.berachain,
    [SUPPORTED_NETWORKS.Soneium]: theme.palette.background.soneium,
    [SUPPORTED_NETWORKS.Abstract]: theme.palette.background.abstract,
    [SUPPORTED_NETWORKS.Shibarium]: theme.palette.background.shibarium,
    [SUPPORTED_NETWORKS.Ten]: theme.palette.background.ten,
    [SUPPORTED_NETWORKS.Monad]: theme.palette.background.monad,
  };

  const selectedNetwork = useMemo(() => {
    if (!Object.values(SUPPORTED_NETWORKS).includes(storedChain)) {
      updateStoredChain(DEFAULT_NETWORK);
      return NETWORK_SELECTION.find((item) => item.value === DEFAULT_NETWORK);
    }
    return NETWORK_SELECTION.find((item) => item.value === storedChain);
  }, [storedChain, updateStoredChain]);
  const SelectedIcon = useMemo(() => {
    const selectedBgColor =
      selectedNetwork.value === SUPPORTED_NETWORKS.Berachain
        ? '#814625'
        : selectedNetwork.value === SUPPORTED_NETWORKS.Soneium
        ? '#000000'
        : selectedNetwork.value === SUPPORTED_NETWORKS.Abstract
        ? 'linear-gradient(180deg, #16CB76 0%, #00C165 100%)'
        : selectedNetwork.value === SUPPORTED_NETWORKS.ApeChain
        ? '#FFFFFF'
        : selectedNetwork.value === SUPPORTED_NETWORKS.Monad
        ? '#000000'
        : NETWORK_FILL[selectedNetwork.value];
    const forceNativeColor = [
      SUPPORTED_NETWORKS.Soneium,
      SUPPORTED_NETWORKS.Abstract,
      SUPPORTED_NETWORKS.Berachain,
      SUPPORTED_NETWORKS.ApeChain,
      SUPPORTED_NETWORKS.Shibarium,
      SUPPORTED_NETWORKS.Monad,
    ].includes(selectedNetwork.value);
    return (
      <NetworkIcon color={selectedBgColor} forceNativeColor={forceNativeColor}>
        {selectedNetwork.icon}
      </NetworkIcon>
    );
  }, [selectedNetwork]);

  useEffect(() => {
    if (isConnected && chainId !== storedChain) {
      const supportedChainIds = Object.values(SUPPORTED_NETWORKS);
      if (supportedChainIds.includes(chainId)) {
        localStorage.setItem('storedChain', chainId);
        setWrongNetwork(false);
        if (!location.pathname.includes('/launchpad')) {
          history.push('/collections?type=top');
        }
        window.location.reload();
        return;
      }
      setWrongNetwork(true);
    } else {
      setWrongNetwork(false);
    }
  }, [isConnected, chainId, storedChain]);

  return (
    <Select
      variant="standard"
      value={storedChain}
      disableUnderline
      IconComponent={() => null}
      onChange={(event) => handleToggleNetwork(event.target.value)}
      renderValue={() => SelectedIcon}
      sx={{
        height: '100%',
        width: '40px',
        '& .MuiSelect-select': {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
        },
      }}
      inputProps={{
        MenuProps: {
          disableScrollLock: true,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          MenuListProps: {
            sx: {
              padding: 0,
              borderRadius: '8px',
            },
          },
          PaperProps: {
            sx: {
              borderRadius: '8px',
              border: `1px solid ${theme.palette.border.separator}`,
            },
          },
        },
      }}
    >
      {NETWORK_SELECTION.map((network) => (
        <MenuItem
          key={network.value}
          value={network.value}
          sx={{
            backgroundColor: `${
              network.value === selectedNetwork.value
                ? theme.palette.background.select
                : theme.palette.background.default
            } !important`,
            padding: '12.5px 10px',

            '&: hover': {
              backgroundColor: `${
                network.value !== selectedNetwork.value && theme.palette.background.hover
              } !important`,
            },
            '& > svg': {
              width: 28,
              height: 28,
            },
            '& svg path':
              network.value === SUPPORTED_NETWORKS.Base
                ? { fill: '#FFFFFF' }
                : network.value === SUPPORTED_NETWORKS.Abstract ||
                  network.value === SUPPORTED_NETWORKS.Soneium ||
                  network.value === SUPPORTED_NETWORKS.Berachain ||
                  network.value === SUPPORTED_NETWORKS.ApeChain ||
                  network.value === SUPPORTED_NETWORKS.Shibarium ||
                  network.value === SUPPORTED_NETWORKS.Monad
                ? {}
                : { fill: NETWORK_FILL[network.value] },
          }}
        >
          {network.value === SUPPORTED_NETWORKS.Base ? (
            <NetworkIcon
              color={NETWORK_FILL[network.value]}
              sx={{
                height: 28,
                minWidth: 28,
                borderRadius: 8,
                mr: 1,
                '& svg': { width: 22, height: 22, padding: 0 },
              }}
            >
              {network.icon}
            </NetworkIcon>
          ) : (
            network.icon
          )}
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.15px',
            }}
          >
            {network.label}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default NetworkSelect;
